import React, { useRef, useCallback } from 'react';
import { Field } from '../../../headless/field';
import { useFieldProps } from '../../../contexts/field-props-context';
import { type PublicFileUploadProps } from '../../../types/public-props';
import { useFileUploadController } from '../use-file-upload-controller';
import { InputContextProvider } from './contexts/input-context';
import { ExplanationText } from './explanation-text';
import { Files } from './files';

const DEFAULT_MAX_FILES = 1;

interface ButtonInputFileUploadProps {
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

const ButtonInputRoot = React.forwardRef<
  HTMLDivElement,
  ButtonInputFileUploadProps
>((props, ref) => {
  const { asChild, className, children } = props;
  const {
    id,
    value,
    required,
    readOnly,
    onChange,
    onBlur,
    onFocus,
    buttonText,
    description,
    explanationText,
    allowedFileFormats,
    maxFiles,
    target,
  } = useFieldProps<PublicFileUploadProps>();
  const descriptionId = description ? `${id}-description` : undefined;
  const valueSliderLimit = maxFiles || DEFAULT_MAX_FILES;
  const multiple = valueSliderLimit > 1;

  const {
    handleChange: handleChangeInController,
    handleBlur,
    accept,
  } = useFileUploadController({
    valueSliderLimit,
    value: value || [],
    target,
    onChange,
    onBlur,
    onFilesCleared: () => {},
    explanationText,
    uploadFileFormats: allowedFileFormats || [],
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleClick = useCallback(() => {
    const input = fileInputRef.current;
    input?.click();
  }, []);

  const handleChange = useCallback(
    (files: FileList | null, setFiles: (files: string[]) => void) => {
      if (!files) {
        return;
      }
      const fileNames = Array.from(files).map((file) => file.name);
      setFiles(fileNames);
      handleChangeInController(files);
    },
    [handleChangeInController],
  );

  return (
    <Field.Input ref={ref} asChild={asChild}>
      <InputContextProvider>
        {(setFiles) => {
          return (
            <>
              <input
                ref={fileInputRef}
                id={id}
                type="file"
                required={required}
                readOnly={readOnly}
                aria-describedby={descriptionId}
                aria-invalid={!!(required && !value)}
                aria-required={required}
                multiple={multiple}
                onChange={(e) => handleChange(e.target.files, setFiles)}
                onBlur={handleBlur}
                onFocus={onFocus}
                style={{
                  display: 'none',
                }}
                accept={accept}
              />

              <button type="button" className={className} onClick={handleClick}>
                {buttonText}
              </button>

              {children}
            </>
          );
        }}
      </InputContextProvider>
    </Field.Input>
  );
});

ButtonInputRoot.displayName = 'FileUpload.Input';

interface ButtonInputFileUploadComponent
  extends React.ForwardRefExoticComponent<
    ButtonInputFileUploadProps & React.RefAttributes<HTMLInputElement>
  > {
  ExplanationText: typeof ExplanationText;
  Files: typeof Files;
}

export const ButtonInput = ButtonInputRoot as ButtonInputFileUploadComponent;
ButtonInput.ExplanationText = ExplanationText;
ButtonInput.Files = Files;
